<script>
    /**
     * SPDX-FileCopyrightText: 2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    import { onDestroy, tick } from 'svelte';

    /**
     * @typedef {Object} Props
     * @property {String} [text]
     * @property {Object} [rect]
     * @property {Object} [config]
     * @property {Number} [refreshKey]
     */
    /** @type {Props} */
    let {
        text = '',
        rect = {
            top: 0,
            left: 0,
            width: 0,
            height: 0
        },
        config = {},
        refreshKey = 0
    } = $props();

    const supportedTypes = ['horizontal', 'diagonal'];
    const initialRepetitionCount = 200;
    const maxRepetitionCount = 16000;
    const textStyleBlockedProperties = [
        'display',
        'height',
        'left',
        'max-height',
        'max-width',
        'min-height',
        'min-width',
        'position',
        'right',
        'rotate',
        'scale',
        'top',
        'transform',
        'transform-origin',
        'translate',
        'width'
    ];
    let contentElement = $state();
    let fillToken = 0;
    let repetitionCount = $state(initialRepetitionCount);
    let type = $derived(supportedTypes.includes(config.type) ? config.type : 'horizontal');
    let safeRect = $derived(rect || {});
    let width = $derived(Math.max(0, numericValue(safeRect.width, 0)));
    let height = $derived(Math.max(0, numericValue(safeRect.height, 0)));

    function numericValue(value, fallback) {
        const numberValue = Number(value);
        return Number.isFinite(numberValue) ? numberValue : fallback;
    }

    function joinStyle(parts, extraStyle) {
        return [...parts, extraStyle].filter(Boolean).join(';');
    }

    function sanitizeTextStyle(style) {
        const styleElement = globalThis.document?.createElement('div');

        if (!styleElement || !style) {
            return style || '';
        }

        styleElement.setAttribute('style', style);
        textStyleBlockedProperties.forEach(property => styleElement.style.removeProperty(property));

        return styleElement.getAttribute('style') || '';
    }

    function getRenderedContentHeight() {
        return contentElement?.scrollHeight || contentElement?.getBoundingClientRect().height || 0;
    }

    function getFillTargetHeight() {
        if (type === 'diagonal') {
            return contentElement?.clientWidth || Math.hypot(width, height);
        }

        return height;
    }

    async function ensureContentFill(token) {
        for (let attempts = 0; attempts < 4; attempts++) {
            await tick();

            if (token !== fillToken || !contentElement || !text) {
                return;
            }

            const fillTargetHeight = getFillTargetHeight();
            const renderedContentHeight = getRenderedContentHeight();

            if (!fillTargetHeight || !renderedContentHeight || renderedContentHeight >= fillTargetHeight) {
                return;
            }

            const scaledRepetitionCount = Math.ceil(repetitionCount * (fillTargetHeight / renderedContentHeight) * 1.1);
            const nextRepetitionCount = Math.min(
                maxRepetitionCount,
                Math.max(repetitionCount + initialRepetitionCount, scaledRepetitionCount)
            );

            if (nextRepetitionCount <= repetitionCount) {
                return;
            }

            repetitionCount = nextRepetitionCount;
        }
    }

    function resetContentFill() {
        repetitionCount = initialRepetitionCount;
        ensureContentFill(++fillToken);
    }

    let layer = $derived(config.layer === 'foreground' ? 'foreground' : 'background');
    let watermarkStyle = $derived(
        joinStyle(
            [
                `top:${Math.max(0, numericValue(safeRect.top, 0))}px`,
                `left:${Math.max(0, numericValue(safeRect.left, 0))}px`,
                `width:${width}px`,
                `height:${height}px`
            ],
            config.containerStyle
        )
    );
    let contentStyle = $derived(sanitizeTextStyle(config.style || ''));
    let fillKey = $derived(
        [text, type, width, height, contentStyle, config.containerStyle || '', refreshKey].join('|')
    );
    let repeatedText = $derived(text ? `${text} `.repeat(repetitionCount) : '');

    $effect(() => {
        if (fillKey) {
            resetContentFill();
        }
    });

    onDestroy(() => {
        fillToken++;
    });
</script>

<style>
    .watermark {
        position: fixed;
        box-sizing: border-box;
        pointer-events: none;
        overflow: hidden;
        container-type: inline-size;
        direction: ltr;
        user-select: none;
    }

    .watermark.foreground {
        z-index: var(--layer-front);
    }

    .watermark.background {
        z-index: var(--layer-0);
    }

    :global(.test-runner.overlay .tao-wmark) {
        display: none;
    }

    .content {
        height: auto;
        overflow: visible;
        white-space: pre-wrap;
        white-space-collapse: preserve;
    }

    /* Keep coverage geometry on .content; config.style customizes the rendered text. */
    .content-text {
        color: #000;
        display: inline !important;
        font-family: 'Courier New', monospace !important;
        font-size: calc(1.25rem * var(--a11y-fontsize-scale, 1));
        transform: none !important;
        width: auto !important;
    }

    .watermark.horizontal .content {
        width: calc(100% + 30em);
    }

    .watermark.horizontal .content-text {
        line-height: 2.5em;
        opacity: 0.1;
    }

    .watermark.diagonal .content {
        width: calc((100cqw + 100cqh) / 1.3);
        /* -71.4 = -100 / sin(45deg) = -100 / sqrt(2). */
        transform: rotate(-45deg) translateX(-71.4285714286cqh);
        transform-origin: top left;
    }

    .watermark.diagonal .content-text {
        line-height: 9em;
        opacity: 0.3;
    }
</style>

{#if repeatedText}
    <div class="tao-wmark watermark {type} {layer}" style={watermarkStyle} aria-hidden="true">
        <div class="content" bind:this={contentElement}>
            <span class="content-text" style={contentStyle}>{repeatedText}</span>
        </div>
    </div>
{/if}
